import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:8000';

function authHeader() {
  const auth = JSON.parse(localStorage.getItem('zocs_auth') || '{}');
  return { Authorization: `Bearer ${auth.token}` };
}

export async function getRiderAssignmentJobs() {
  const res = await axios.get(`${API_BASE}/dispatch/rider-assignment/jobs`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function listAssignableRiders() {
  const res = await axios.get(`${API_BASE}/dispatch/rider-assignment/riders`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function assignJobToRider(appointmentId, riderId, googleMapsLink) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-assignment/assign`,
    { appointment_id: appointmentId, rider_id: riderId, google_maps_link: googleMapsLink || undefined },
    { headers: authHeader() }
  );
  return res.data;
}

// Several appointments to one rider as one trip, in visiting order. `googleMapsLinks` is
// keyed by appointment id, same overrides the single assign sends one at a time.
export async function assignTripToRider(appointmentIds, riderId, googleMapsLinks, boltOnIds) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-assignment/assign-trip`,
    {
      appointment_ids: appointmentIds,
      rider_id: riderId,
      google_maps_links: googleMapsLinks || undefined,
      bolt_on_appointment_ids: boltOnIds?.length ? boltOnIds : undefined,
    },
    { headers: authHeader() }
  );
  return res.data;
}

// Today's orders with no pack yet: each can ride on the stop before it in a trip and
// take that bag's leftovers at a hand-on.
export async function getBoltOnCandidates() {
  const res = await axios.get(`${API_BASE}/dispatch/rider-assignment/bolt-on-candidates`, {
    headers: authHeader(),
  });
  return res.data;
}

// Add an order with no pack to one that is already dispatched and out with a rider. The rider
// keeps their bag and gets one more stop; the rider backend says in a sentence when it can't.
export async function addBoltOnToDispatched(appointmentId, boltOnAppointmentId, googleMapsLink) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-assignment/add-bolt-on`,
    {
      appointment_id: appointmentId,
      bolt_on_appointment_id: boltOnAppointmentId,
      google_maps_link: googleMapsLink || undefined,
    },
    { headers: authHeader() }
  );
  return res.data;
}

// The two ways to close a rider job, keyed on source_job_id because the Live Jobs
// board always has one and does not always have a ZOCS appointment.
//
// Which one is truthful depends on how far the visit got. Completing a job the rider
// never started credits them with a visit they did not make; cancelling one they did
// make takes a completed job off their dashboard and notifies them it was cancelled.
// See VISIT_HAPPENED_STATUSES in pages/RiderAssignment.jsx.
export async function completeRiderJob(sourceJobId, { outcome, reason } = {}) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-admin/jobs/${encodeURIComponent(sourceJobId)}/complete`,
    { outcome: outcome || undefined, reason: reason || undefined },
    { headers: authHeader() }
  );
  return res.data;
}

export async function cancelRiderJob(sourceJobId, { reason, outcome } = {}) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-admin/jobs/${encodeURIComponent(sourceJobId)}/cancel`,
    { reason: reason || undefined, outcome: outcome || undefined },
    { headers: authHeader() }
  );
  return res.data;
}

// `reason` reaches the rider's own cancellation notice, so pass something that
// reads as an explanation. Omitted, the backend falls back to a generic string.
export async function cancelJobRiderOffer(appointmentId, reason) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-assignment/cancel`,
    { appointment_id: appointmentId, reason: reason || undefined },
    { headers: authHeader() }
  );
  return res.data;
}

// ── Rider operations console ───────────────────────────────────────────────
// Proxied through ZOCS so the browser only ever talks to one origin and the
// rider staff token never reaches client-side code.

export async function getRiderAdminOverview() {
  const res = await axios.get(`${API_BASE}/dispatch/rider-admin/overview`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function getRiderAdminCash() {
  const res = await axios.get(`${API_BASE}/dispatch/rider-admin/cash`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function collectRiderCash(checkoutIds, note) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-admin/cash/collect`,
    { checkout_ids: checkoutIds, note: note || null },
    { headers: authHeader() }
  );
  return res.data;
}

export async function getRiderAdminVisits() {
  const res = await axios.get(`${API_BASE}/dispatch/rider-admin/visits`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function endRiderShift(riderId, reason) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-admin/shifts/end`,
    { rider_id: riderId, reason: reason || null },
    { headers: authHeader() }
  );
  return res.data;
}

export async function getRiderAdminIssues() {
  const res = await axios.get(`${API_BASE}/dispatch/rider-admin/issues`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function resolveRiderIssue(issueId, note) {
  const res = await axios.post(
    `${API_BASE}/dispatch/rider-admin/issues/${issueId}/resolve`,
    { note },
    { headers: authHeader() }
  );
  return res.data;
}

export async function getRiderIssueAudioBlob(issueId) {
  const res = await axios.get(
    `${API_BASE}/dispatch/rider-admin/issues/${issueId}/audio`,
    { headers: authHeader(), responseType: 'blob' }
  );
  return res.data;
}

export async function getRtoFeedbackAudioBlob(appointmentUid) {
  const res = await axios.get(
    `${API_BASE}/dispatch/rider-admin/rto-feedback/${encodeURIComponent(appointmentUid)}/audio`,
    { headers: authHeader(), responseType: 'blob' }
  );
  return res.data;
}
